"use client";

import * as React from "react";
import Image from "next/image";
import {
  TESTIMONIALS,
  type Testimonial,
  type RoleSegment,
} from "@/data/testimonials";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { ExternalLink } from "@/components/ui/external-link";

function formatFollowers(count: number): string {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  }
  return count.toString();
}

function QuoteIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
    </svg>
  );
}

function RoleDisplay({ segments }: { segments: RoleSegment[] }) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.url ? (
          <ExternalLink
            key={index}
            href={segment.url}
            className="hover:text-foreground transition-colors"
          >
            {segment.text}
          </ExternalLink>
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
    </>
  );
}

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  const avatarUrl = `https://avatars.githubusercontent.com/${testimonial.username}?s=96`;

  return (
    <article
      data-testid="testimonial-card"
      className="group relative rounded-lg border border-[rgba(0,0,0,0.05)] bg-[rgba(0,0,0,0.02)] p-4 transition-all duration-300 ease-out hover:bg-[rgba(0,0,0,0.02)] dark:border-[rgba(255,255,255,0.05)] dark:bg-[#0f1011] dark:hover:bg-[#0f1011]"
    >
      <QuoteIcon className="text-brand/15 group-hover:text-brand/25 absolute -top-1 -left-1 size-10 transition-colors duration-300" />

      <div className="relative flex gap-3">
        <ExternalLink
          href={testimonial.profileUrl}
          className="shrink-0 no-underline"
          data-testid="testimonial-avatar"
        >
          <Image
            src={avatarUrl}
            alt={`${testimonial.name || testimonial.username}'s profile`}
            width={44}
            height={44}
            className="ring-border/60 rounded-full ring-1 transition-all duration-300"
          />
        </ExternalLink>

        <div className="min-w-0 flex-1">
          <ExternalLink
            href={testimonial.profileUrl}
            className="no-underline hover:no-underline"
          >
            <span className="text-foreground hover:text-foreground text-base font-semibold transition-colors">
              {testimonial.name || `@${testimonial.username}`}
            </span>
          </ExternalLink>

          <p className="text-muted-foreground mt-0.5 text-base">
            <RoleDisplay segments={testimonial.role} />
          </p>

          <p className="text-muted-foreground mt-0.5 text-base">
            {formatFollowers(testimonial.followers)} followers
          </p>
        </div>
      </div>

      <blockquote className="dark:border-border/60 mt-3 border-t border-[rgba(0,0,0,0.08)] pt-3">
        <ExternalLink
          href={testimonial.commentUrl}
          className="text-muted-foreground hover:text-foreground block text-base/relaxed no-underline transition-colors hover:no-underline"
        >
          &ldquo;{testimonial.quote}&rdquo;
        </ExternalLink>
      </blockquote>
    </article>
  );
}

export function Testimonials() {
  const initialTestimonialCount = 6;
  const featuredTestimonials = TESTIMONIALS.slice(0, initialTestimonialCount);
  const remainingTestimonials = TESTIMONIALS.slice(initialTestimonialCount);
  const [showAll, setShowAll] = React.useState(false);
  const [hasExpanded, setHasExpanded] = React.useState(false);

  const toggleRemainingTestimonials = () => {
    if (!showAll) {
      setHasExpanded(true);
    }
    setShowAll(!showAll);
  };

  return (
    <section aria-labelledby="testimonials-heading" className="py-2">
      <div className="mx-auto max-w-7xl">
        <div className="mb-4">
          <h2
            id="testimonials-heading"
            className="text-xl font-semibold tracking-tight sm:text-2xl"
          >
            Notable Feedback
          </h2>
          <p className="text-muted-foreground mt-0.5 text-base">
            From open source maintainers, project leads, and contributors.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {featuredTestimonials.map((testimonial) => (
            <TestimonialCard
              key={testimonial.commentUrl}
              testimonial={testimonial}
            />
          ))}
        </div>

        {remainingTestimonials.length > 0 && (
          <>
            <div
              id="remaining-testimonials"
              data-testid="testimonials-expanded-container"
              // Animating grid-template-rows from 0fr to 1fr reaches the
              // content's real height, unlike the previous max-height cap,
              // which silently clipped the tail of the list once it grew past
              // the hardcoded value.
              // After the first expansion the cards stay mounted. When they
              // collapse, inert keeps their links out of the tab order and
              // the accessibility tree without interfering with the animation.
              inert={!showAll}
              className={`mt-3 grid transition-all duration-300 ease-out motion-reduce:transition-none ${showAll ? "mb-3 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"} `}
            >
              <div className="overflow-hidden">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {hasExpanded &&
                    remainingTestimonials.map((testimonial) => (
                      <TestimonialCard
                        key={testimonial.commentUrl}
                        testimonial={testimonial}
                      />
                    ))}
                </div>
              </div>
            </div>

            <div className="text-center">
              <Button
                variant="outline"
                onClick={toggleRemainingTestimonials}
                data-testid="testimonials-show-more-button"
                className="rounded-full"
                aria-expanded={showAll}
                aria-controls="remaining-testimonials"
              >
                <span>
                  {showAll
                    ? "Show less"
                    : `Show ${remainingTestimonials.length} more`}
                </span>
                <ChevronDown
                  className={`ml-2 size-4 transition-transform duration-300 ${showAll ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </Button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
